import { Module } from "vuex";
import * as firebase from 'firebase/app';
import 'firebase/firestore';
import { serializer } from '@/api/shared/serializer';
import { NotificationTypeEnum } from "@/enums/NotificationTypeEnum";
import moment from "moment";
import {StaffAccessLevels} from "@/enums/StaffAccessLevels";

const userModule: Module<any, any> = {
  namespaced: false,
  state: {
    users: [],
    maintenanceUsers: [],
    maintenanceVendors: [],
    selecteduser: null,
    facilitiesForOrg: [],
    incomingWebNotifications: [],
    usersRoles: [],
  },

  mutations: {
    setusers(state: any, payload: []) {
      state.users = payload;
    },
    setFacilitiesForOrg(state: any, payload: []) {
      state.facilitiesForOrg = payload;
    },

    setSelecteduser(state: any, payload: []) {
      state.selecteduser = payload;
    },

    setWebNotifications(state: any, payload: []) {
      state.incomingWebNotifications = payload;
    },

    setUsersRole(state: any, payload: []) {
      state.usersRoles = payload
    },

    setMaintenanceUsers(state: any, payload: []) {
      state.maintenanceUsers = payload;
    },

    setMaintenanceVendors(state: any, payload: []) {
      state.maintenanceVendors = payload;
    }
  },

  actions: {
    async getMaintenanceUsers({ commit }) {
      const facilityId = this.state.authModule.currentFacility;
      const users = firebase
        .firestore()
        .collection("users")
        .where("facilitiesIds", "array-contains", facilityId)
        .where("orgId", "==", this.state.authModule.currentFacilityData.orgId)
        .where("accessLevel", "in", [StaffAccessLevels.MaintenanceAdmin, StaffAccessLevels.MaintenanceUser]);

      await users.get().then((querySnapshot) => {
        const usersList = serializer.querySnapshotToDocuments(querySnapshot);
        commit("setMaintenanceUsers", usersList);
      });
    },

    async getMaintenanceVendors({ commit }) {
      const facilityId = this.state.authModule.currentFacility;
      const users = firebase
        .firestore()
        .collection("vendors")
        .where("facilityId", "==", facilityId)
        .where("type", "==", "Maintenance")

      await users.get().then((querySnapshot) => {
        const usersList = serializer.querySnapshotToDocuments(querySnapshot);
        commit("setMaintenanceVendors", usersList);
      });
    },

    async getFacilitiesForOrg({ commit }, orgId = null ) {
      const facility = this.state.authModule.currentFacilityData;
      const facilities = firebase.firestore().collection("facility").where("orgId", "==", orgId ?? facility.orgId);
      await facilities.get().then((querySnapshot) => {
        const facilitiesList: Array<any> = [];

        querySnapshot.docs.forEach((x) => {
          const thisRes = x.data();
          thisRes.id = x.id;
          facilitiesList.push(thisRes);
        });
        commit("setFacilitiesForOrg", facilitiesList);
      });
    },

    async getIncomingWebNotifications({ commit }) {
      
      if (!this.state.authModule.user.options || !this.state.authModule.user.options.webNotifications) return

      const facilityId = this.state.authModule.currentFacility

      const userWebNotificationsOptions = this.state.authModule.user.options.webNotifications

      const user = this.state.authModule.user;
      const userId = this.state.authModule.user.id;

      const fromDate: any = user?.createdDateTime ? moment(user?.createdDateTime.seconds * 1000).toDate() : null;

      let query;
      if(fromDate) {
        query = firebase.firestore().collection("notifications")
          .where("facilityId", "==", facilityId)
          .where("to", "==", "staff")
          .where("dateTimeCreated", ">", fromDate)
          .orderBy("dateTimeCreated", "desc")
      } else {
        query = firebase.firestore().collection("notifications")
          .where("facilityId", "==", facilityId)
          .where("to", "==", "staff")
          .orderBy("dateTimeCreated", "desc")
      }

      const queryResult = await query.get()

      let notifications = serializer.querySnapshotToDocuments(queryResult) as any[];

      if (!userWebNotificationsOptions.serviceRequestCreated) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.ServiceRequestCreated)
      } if (!userWebNotificationsOptions.serviceRequestCompleted) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.ServiceRequestCompleted)
      } if (!userWebNotificationsOptions.unattendedEventEnded) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.UnattendedEventEnded)
      } if (!userWebNotificationsOptions.maintenanceRequestCreated) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.MaintenanceRequestCreated)
      } if (!userWebNotificationsOptions.deviceAssigned) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.DeviceAssigned)
      } if (!userWebNotificationsOptions.deviceUnassigned) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.DeviceUnassigned)
      } if (!userWebNotificationsOptions.visitToResidentCreated) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.VisitCreated)
      } if (!userWebNotificationsOptions.bookingsAndOrdersComment) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.BookingsAndOrdersComment)
      } if (!userWebNotificationsOptions.messagesFromFamily) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.Message && x.from != "family")
      }

      notifications = notifications.filter(x => (!x.readByStaffs || !x.readByStaffs.length || !x.readByStaffs.find(s => s.id === userId))
      && !x.staffAppNotification)

      console.log(`loaded (${notifications.length}) notifications`)

      commit("setWebNotifications", notifications);
    },

    async updateUser({ dispatch, commit }, user) {
      await firebase
        .firestore()
        .collection("users")
        .doc(user.id)
        .update(user)
        .then(async() => {
          const updatedUser = await dispatch("getSelectedUser", user.id)
          if(this.state.authModule.user.id === user.id) {
            dispatch("setUserData", updatedUser);
          }
        });
    },

    async deleteUser({ dispatch, commit }, id) {
      return await firebase
        .firestore()
        .collection("users")
        .doc(id)
        .delete().then(() => {
          dispatch("getCalenderEvents");
          return true;
        })
    },

    async getSelectedUser({ commit }, id) {
      const user = firebase.firestore().collection("users").doc(id);

        return await user.get().then((querySnapshot) => {
          const currentUser = querySnapshot.data()
          if (currentUser != null) {
            currentUser.id = querySnapshot.id
            commit("setSelecteduser", currentUser)
            return currentUser
          }
      })
    },

    async updateFacilityName(_, data) {
      return await firebase
        .firestore()
        .collection("users")
        .where("orgId", "==", data.facilityOrgId)
        .where("facilitiesIds", 'array-contains', data.facilityId)
        .get()
        .then(snapshot => {
          snapshot.forEach(async doc => {
            const user = doc.data();
            const userId = doc.id;
            if (user.facilitiesIds.find(x => x === data.facilityId)) {
              const facility = user.facilities.find(x => x.id == data.facilityId);
              facility.name = data.name;

              const facilitiesAll = user.facilities.filter(x => x.id != data.facilityId) || [];
              facilitiesAll.push(facility);

              if (facilitiesAll.length) {
                return await firebase
                  .firestore()
                  .collection("users")
                  .doc(userId)
                  .update({
                    facilities: facilitiesAll
                  }).catch(err => {
                    console.error(err)
                  })
              }
            }
          })
        })
    },

    async getUsersRoles({commit}) {
      return await firebase
        .firestore()
        .collection("usersRoles")
        .where("orgId", "==", this.state.authModule.currentFacilityData.orgId)
        .get().then((querySnapshot) => {
          const result: any[] = [];

          querySnapshot.docs.forEach(doc => {
            const role = doc.data();
            role.id = doc.id;
            result.push(role)
          });

          commit("setUsersRole", result);
        })
    },

    async addUserRole({dispatch}, role) {
      return await firebase
        .firestore()
        .collection("usersRoles")
        .add(role)
        .then(async () => {
          dispatch("getUsersRoles")
        });
    },

    async deleteUserRole({dispatch}, roleId) {
      await firebase
        .firestore()
        .collection("usersRoles")
        .doc(roleId)
        .delete();

      return await firebase
        .firestore()
        .collection("users")
        .where("orgId", "==", this.state.authModule.currentFacilityData.orgId)
        .where("roleId", "==", roleId)
        .get()
        .then((querySnapshot) => {
          querySnapshot.docs.forEach(doc => {
            doc.ref.update({
              role: firebase.firestore.FieldValue.delete(),
              roleId: firebase.firestore.FieldValue.delete()
            }).then(() => {
              dispatch("getUsersRoles");
            })
          })
        })
    },

    async updateUserRole({dispatch}, role) {
      await firebase
        .firestore()
        .collection("usersRoles")
        .doc(role.id)
        .update({
          name: role.name,
        });

      return await firebase
        .firestore()
        .collection("users")
        .where("orgId", "==", this.state.authModule.currentFacilityData.orgId)
        .where("roleId", "==", role.id)
        .get()
        .then((querySnapshot) => {
          querySnapshot.docs.forEach(doc => {
            doc.ref.update({
              role: role.name
            }).then(() => {
              dispatch("getUsersRoles");
            });
          });
        });
    }

  },
};
export default userModule;
